import { Controller } from "@hotwired/stimulus";

// Brand source picker in the project wizard's asset step.
//
// Picking clips here does not build a second list: each tile hands a
// descriptor to upload-preview, which adds it to the same array uploaded
// files live in. That is what makes one strip, one duration meter, one review
// count and one drag order cover every source.
export default class extends Controller {
  static targets = [
    "panel", "body", "card", "useButton", "tile", "count", "selectAll",
    "brandField", "logoField"
  ];

  static values = {
    // brand_asset ids already copied into this draft, so reopening it shows
    // those clips ticked instead of offering them again.
    usedIds: { type: Array, default: [] }
  };

  connect() {
    this.#markAlreadyUsed();
    this.#openSelectedBrand();
  }

  // ── Panels ──

  togglePanel(event) {
    const panel = this.#panelOf(event.currentTarget);
    const body = panel.querySelector("[data-brand-picker-target='body']");
    this.#setOpen(panel, body.hidden);
  }

  // ── Brand choice ──

  chooseBrand(event) {
    const panel = this.#panelOf(event.currentTarget);
    const alreadyChosen = this.brandFieldTarget.value === panel.dataset.brandId;
    this.#choose(alreadyChosen ? null : panel);
  }

  dropBrandLogo() {
    this.#clearBrandLogo();
  }

  // ── Clips ──

  toggleTile(event) {
    const tile = event.currentTarget;
    tile.dataset.selected === "true" ? this.#deselect(tile) : this.#select(tile);
    this.#renderCount();
  }

  selectAll(event) {
    const tiles = this.#tilesIn(this.#panelOf(event.currentTarget));
    // One button that fills and then empties the selection; the label flips to
    // match, so the action is never a surprise.
    const allSelected = tiles.every((t) => t.dataset.selected === "true");
    tiles.forEach((t) => (allSelected ? this.#deselect(t) : this.#select(t)));
    this.#renderCount();
  }

  // upload-preview fires this when a clip is removed from the strip, so the
  // picker's ticks can never drift out of sync with what is actually selected.
  onFilmstripRemoval(event) {
    const tile = this.tileTargets.find((t) => t.dataset.assetId === String(event.detail.id));
    if (!tile) return;

    tile.dataset.selected = "false";
    tile.setAttribute("aria-pressed", "false");
    this.#renderCount();
  }

  // ── Internals ──

  // Clips carried over from a saved draft are already in the strip, so they
  // are ticked without dispatching — dispatching would add a duplicate.
  #markAlreadyUsed() {
    this.usedIdsValue.forEach((id) => {
      const tile = this.tileTargets.find((t) => t.dataset.assetId === String(id));
      if (!tile) return;
      tile.dataset.selected = "true";
      tile.setAttribute("aria-pressed", "true");
    });
  }

  #openSelectedBrand() {
    const panel = this.#chosenPanel();
    if (panel) this.#setOpen(panel, true);
    this.#renderChosen();
    this.#renderCount();
  }

  #setOpen(panel, open) {
    panel.querySelector("[data-brand-picker-target='body']").hidden = !open;
    panel.querySelector("[data-brand-picker-target='card']").setAttribute("aria-expanded", open ? "true" : "false");
  }

  // Adopting a brand adopts its logo — that is the common intent, and the
  // notice under LOGO is where it gets undone.
  #choose(panel) {
    this.brandFieldTarget.value = panel ? panel.dataset.brandId : "";
    this.#renderChosen();

    if (panel && panel.dataset.hasLogo === "true") {
      this.#useBrandLogo(panel.dataset.brandName, panel.dataset.brandLogoUrl);
    } else {
      this.#clearBrandLogo();
    }
  }

  #renderChosen() {
    this.panelTargets.forEach((panel) => {
      const chosen = panel.dataset.brandId === this.brandFieldTarget.value;
      panel.classList.toggle("brand-source__brand--active", chosen);

      const button = this.useButtonTargets.find((b) => panel.contains(b));
      if (!button) return;
      button.setAttribute("aria-pressed", chosen ? "true" : "false");
      button.textContent = chosen ? button.dataset.labelUsed : button.dataset.labelUse;
    });
  }

  #select(tile) {
    // Picking a clip with no brand chosen yet means using that brand.
    if (!this.brandFieldTarget.value) this.#choose(this.#panelOf(tile));
    tile.dataset.selected = "true";
    tile.setAttribute("aria-pressed", "true");
    this.dispatch("addBrandAsset", { target: window, detail: this.#descriptor(tile) });
  }

  #deselect(tile) {
    tile.dataset.selected = "false";
    tile.setAttribute("aria-pressed", "false");
    this.dispatch("removeBrandAsset", { target: window, detail: { id: Number(tile.dataset.assetId) } });
  }

  // The notice lives under LOGO, outside this controller's section, so the
  // state travels as an event and upload-preview — which owns the whole form —
  // renders it. Same round-trip the clip ticks already use.
  #useBrandLogo(name, logoUrl) {
    this.logoFieldTarget.value = "1";
    this.dispatch("brandLogo", {
      target: window,
      detail: { active: true, name: name, logoUrl: logoUrl || null }
    });
  }

  #clearBrandLogo() {
    this.logoFieldTarget.value = "0";
    this.dispatch("brandLogo", { target: window, detail: { active: false } });
  }

  #descriptor(tile) {
    const d = tile.dataset;
    return {
      id: Number(d.assetId),
      name: d.assetName,
      size: Number(d.assetSize),
      contentType: d.assetContentType,
      url: d.assetUrl,
      thumbUrl: d.assetThumbUrl || null,
      posterUrl: d.assetPosterUrl || null,
      width: d.assetWidth ? Number(d.assetWidth) : null,
      height: d.assetHeight ? Number(d.assetHeight) : null,
      durationMs: d.assetDurationMs ? Number(d.assetDurationMs) : null
    };
  }

  #panelOf(el) {
    return el.closest("[data-brand-picker-target='panel']");
  }

  #chosenPanel() {
    return this.panelTargets.find((p) => p.dataset.brandId === this.brandFieldTarget.value) || null;
  }

  #tilesIn(panel) {
    return panel ? this.tileTargets.filter((t) => panel.contains(t)) : [];
  }

  #renderCount() {
    this.panelTargets.forEach((panel) => {
      const tiles = this.#tilesIn(panel);
      const selected = tiles.filter((t) => t.dataset.selected === "true").length;

      this.countTargets.filter((el) => panel.contains(el)).forEach((el) => {
        el.textContent = el.dataset.template
          .replace("%{selected}", selected)
          .replace("%{total}", tiles.length);
      });

      this.selectAllTargets.filter((btn) => panel.contains(btn)).forEach((btn) => {
        const allSelected = tiles.length > 0 && selected === tiles.length;
        btn.textContent = allSelected ? btn.dataset.labelClear : btn.dataset.labelAll;
      });
    });
  }
}
